import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL2090_R01Store } from "./Store_SAL2090_R01";

import { defaultSAL2090_R01Dto, SAL2090_R01Dto } from "./Dto_SAL2090_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL2090_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL2090_R01Dto> | null>;
}

export const Table_SAL2090_R01 = (props: Props) => {

	const { gridRef } = props;

	const { 
		SAL2090_R01List, 
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useSAL2090_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		settingRowIndex, 
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'salBillNo',
		gridData: SAL2090_R01List,
		defaultGridData: defaultSAL2090_R01Dto,
		selectedRow: undefined,
	});

	// ✅ 복합 키로 고유 ID 생성: salBillNo + customCd + itemCd
	const getRowId = useCallback((params: any) => {
		const data = params.data;
		if (data) {
			return `${data.salBillNo || ''}_${data.customCd || ''}_${data.itemCd || ''}`;
		}
		return params.id;
	}, []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		salesDt: '날짜',
		customCd: '거래처',
		customNm: '상호',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		salesQty: '수량',
		unitNm: '단위',
		salesPrice: '단가',
		dcAmt: '할인액',
		itemAmt: '매출금액',
		remark: '비고',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<SAL2090_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '날짜', colId: 'salesDt', field: 'salesDt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'salesDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params: ValueFormatterParams<SAL2090_R01Dto>) => {
				if (!params.value) return '';
				try {
					const date = new Date(params.value);
					return formatDate(date);
				} catch {
					return String(params.value);
				}
			},
			comparator: cellDateComparator,
		},
		{
			headerName: '거래처', colId: 'customCd', field: 'customCd', width: 100,
			cellStyle: (p) => getCellStyle(p, 'customCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 150,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 150,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '수량', colId: 'salesQty', field: 'salesQty', width: 100,
			cellStyle: (p) => getCellStyle(p, 'salesQty'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '단위', colId: 'unitNm', field: 'unitNm', width: 80,
			cellStyle: (p) => getCellStyle(p, 'unitNm'),
			headerClass: aggridHeaderClass,
			valueGetter: (params) => params.data?.unitNm || params.data?.unit || '',
		},
		{
			headerName: '단가', colId: 'salesPrice', field: 'salesPrice', width: 120,
			cellStyle: (p) => getCellStyle(p, 'salesPrice'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '할인액', colId: 'dcAmt', field: 'dcAmt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'dcAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
			valueFormatter: (params: ValueFormatterParams<SAL2090_R01Dto>) => {
				if (!params.value || params.value === 0) return '-';
				return params.value.toLocaleString();
			},
		},
		{
			headerName: '매출금액', colId: 'itemAmt', field: 'itemAmt', width: 130,
			cellStyle: (p) => getCellStyle(p, 'itemAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 150,
			cellStyle: (p) => getCellStyle(p, 'remark'),
			headerClass: aggridHeaderClass,
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL2090_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL2090_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: SAL2090_R01List.length.toLocaleString() + "건",
		salesQty: SAL2090_R01List.reduce((sum, item) => (sum + (item.salesQty || 0)), 0).toLocaleString(),
		dcAmt: SAL2090_R01List.reduce((sum, item) => (sum + (item.dcAmt || 0)), 0).toLocaleString(),
		itemAmt: SAL2090_R01List.reduce((sum, item) => (sum + (item.itemAmt || 0)), 0).toLocaleString(),
	} as any]), [SAL2090_R01List]);

	return (
		<GridContextWrapper<SAL2090_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={() => {}}
			defaultRowData={defaultSAL2090_R01Dto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL2090_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SAL2090_R01Dto>({
					idKey: 'salBillNo',
					selectedList: [],
					deletedList: [],
				})}
				onRowClicked={undefined}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
